import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

/** Where an admin-only group session (AuthService.ssoAdminOnly) may go, and where it lands otherwise. */
export const ADMIN_ONLY_HOME = '/admin/users';
const isAdminUrl = (url: string) => url === '/admin' || url.startsWith('/admin/') || url.startsWith('/admin?');

/**
 * The session check below, then one more rule: an Ezfy administrator signed in without a pharmacy profile
 * (no `ezfyweb:access`) sees the admin pages only, so every other page sends them to Utilizadores registados.
 */
export const authGuard: CanActivateFn = (route, state): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  return sessionGuard(route, state).pipe(
    map((ok) => (ok && authService.ssoAdminOnly() && !isAdminUrl(state.url) ? router.parseUrl(ADMIN_ONLY_HOME) : ok))
  );
};

const sessionGuard = (...[route, state]: Parameters<CanActivateFn>): Observable<boolean> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Determine if this is an auth route (login, signup, root)
  const isAuthRoute = state.url === '/' || state.url === '/login' || state.url === '/signup';

  // If refresh is in progress, wait for it to complete first
  if (authService.isRefreshing()) {
    return authService.waitForRefreshComplete().pipe(
      switchMap((refreshCompleted) => {
        if (!refreshCompleted) {
          // Refresh failed or timed out
          authService.clearAuth();
          if (isAuthRoute) {
            return of(true);
          }
          router.navigate(['/']);
          return of(false);
        }
        // Refresh completed, check authentication
        const hasValidTokens = authService.isAuthenticatedCheck();
        if (hasValidTokens) {
          if (isAuthRoute) {
            router.navigate(['/dashboard']);
            return of(false);
          }
          return of(true);
        }
        // Still no valid tokens after refresh
        if (isAuthRoute) {
          return of(true);
        }
        router.navigate(['/']);
        return of(false);
      }),
      catchError(() => {
        // Error waiting for refresh
        authService.clearAuth();
        if (isAuthRoute) {
          return of(true);
        }
        router.navigate(['/']);
        return of(false);
      })
    );
  }

  // Check if user has valid tokens
  const hasValidTokens = authService.isAuthenticatedCheck();
  
  if (hasValidTokens) {
    if (isAuthRoute) {
      // User is authenticated but trying to access login/auth pages
      router.navigate(['/dashboard']);
      return of(false);
    }
    // User is authenticated and accessing protected route
    return of(true);
  }

  // Token is expired but we have a refresh token
  // Don't refresh here - let the interceptor handle it on first API call
  // Just check if we have a refresh token to determine if user should be allowed through
  const refreshToken = authService.getRefreshToken();
  if (refreshToken) {
    // We have a refresh token, allow navigation
    // The interceptor will handle token refresh when the first API call is made
    if (isAuthRoute) {
      // User has refresh token but expired access token - redirect to dashboard
      // The interceptor will refresh on first API call
      router.navigate(['/dashboard']);
      return of(false);
    }
    // Allow access - interceptor will refresh token on first API call
    return of(true);
  }

  if (isAuthRoute) {
    // No refresh token or token expired, allow access to login page
    return of(true);
  }

  // No valid tokens and no refresh token, redirect to login
  router.navigate(['/']);
  return of(false);
}; 